import React, { useState, useEffect } from 'react';

interface UnifiedDashboardPreviewProps {
  onClick?: () => void;
}

type UserRole = 'student' | 'teacher' | 'parent' | 'admin';

const UnifiedDashboardPreview: React.FC<UnifiedDashboardPreviewProps> = ({ onClick }) => {
  const [activeRole, setActiveRole] = useState<UserRole>('student');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const roles: { role: UserRole; label: string; icon: string; color: string }[] = [
    { role: 'student', label: 'Student', icon: '🎓', color: '#3b82f6' },
    { role: 'teacher', label: 'Teacher', icon: '👩‍🏫', color: '#10b981' },
    { role: 'parent', label: 'Parent', icon: '👨‍👩‍👧', color: '#ec4899' },
    { role: 'admin', label: 'Admin', icon: '⚙️', color: '#f59e0b' },
  ];

  // Auto-cycle through roles
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveRole(prev => {
          const currentIdx = roles.findIndex(r => r.role === prev);
          return roles[(currentIdx + 1) % roles.length].role;
        });
        setIsTransitioning(false);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const getActiveColor = () => {
    return roles.find(r => r.role === activeRole)?.color || '#3b82f6';
  };

  const renderDashboardContent = () => {
    switch (activeRole) {
      case 'student':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
              <div className="flex items-center gap-2">
                <span className="text-sm">🔥</span>
                <span className="text-xs" style={{ color: '#e2e8f0' }}>7 Day Streak</span>
              </div>
              <span className="text-xs font-bold" style={{ color: '#f59e0b' }}>4,250 XP</span>
            </div>
            <div className="p-2 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              <div className="text-[10px] mb-1" style={{ color: '#64748b' }}>Today's Progress</div>
              <div className="flex gap-1">
                {[85, 92, 70, 45].map((val, i) => (
                  <div key={i} className="flex-1">
                    <div className="h-12 rounded relative overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div
                        className="absolute bottom-0 left-0 right-0 rounded transition-all"
                        style={{ height: `${val}%`, background: '#3b82f6' }}
                      />
                    </div>
                    <div className="text-[8px] text-center mt-1" style={{ color: '#64748b' }}>
                      {['Math', 'Eng', 'Sci', 'Hist'][i]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-2 rounded-lg text-center" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                <div className="text-lg font-bold" style={{ color: '#10b981' }}>12</div>
                <div className="text-[9px]" style={{ color: '#64748b' }}>Badges</div>
              </div>
              <div className="flex-1 p-2 rounded-lg text-center" style={{ background: 'rgba(139, 92, 246, 0.1)' }}>
                <div className="text-lg font-bold" style={{ color: '#8b5cf6' }}>#3</div>
                <div className="text-[9px]" style={{ color: '#64748b' }}>Class Rank</div>
              </div>
            </div>
          </div>
        );
      case 'teacher':
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
                <div className="text-lg font-bold" style={{ color: '#10b981' }}>28</div>
                <div className="text-[9px]" style={{ color: '#64748b' }}>Students</div>
              </div>
              <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                <div className="text-lg font-bold" style={{ color: '#3b82f6' }}>76%</div>
                <div className="text-[9px]" style={{ color: '#64748b' }}>Avg Mastery</div>
              </div>
              <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                <div className="text-lg font-bold" style={{ color: '#ef4444' }}>3</div>
                <div className="text-[9px]" style={{ color: '#64748b' }}>Alerts</div>
              </div>
            </div>
            <div className="p-2 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <div className="flex items-center gap-2">
                <span className="text-sm">⚠️</span>
                <div>
                  <div className="text-[10px] font-medium" style={{ color: '#ef4444' }}>Intervention Needed</div>
                  <div className="text-[9px]" style={{ color: '#94a3b8' }}>3 students struggling with fractions</div>
                </div>
              </div>
            </div>
            <div className="p-2 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              <div className="text-[10px] font-medium mb-1" style={{ color: '#64748b' }}>Class Performance</div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                <div className="h-full rounded-full" style={{ width: '76%', background: 'linear-gradient(90deg, #10b981, #3b82f6)' }} />
              </div>
            </div>
          </div>
        );
      case 'parent':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'rgba(236, 72, 153, 0.1)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm" style={{ background: '#ec4899' }}>E</div>
              <div>
                <div className="text-xs font-medium" style={{ color: '#e2e8f0' }}>Emma's Progress</div>
                <div className="text-[10px]" style={{ color: '#64748b' }}>Year 4 • Last active 2h ago</div>
              </div>
            </div>
            <div className="p-2 rounded-lg" style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
              <div className="flex items-center gap-2">
                <span className="text-sm">🏆</span>
                <div>
                  <div className="text-[10px] font-medium" style={{ color: '#10b981' }}>Achievement Unlocked!</div>
                  <div className="text-[9px]" style={{ color: '#94a3b8' }}>Emma earned "Math Whiz" badge</div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
                <div className="text-[10px]" style={{ color: '#64748b' }}>Mathematics</div>
                <div className="text-sm font-bold" style={{ color: '#3b82f6' }}>85%</div>
              </div>
              <div className="p-2 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
                <div className="text-[10px]" style={{ color: '#64748b' }}>English</div>
                <div className="text-sm font-bold" style={{ color: '#8b5cf6' }}>92%</div>
              </div>
            </div>
          </div>
        );
      case 'admin':
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                <div className="text-lg font-bold" style={{ color: '#f59e0b' }}>1,247</div>
                <div className="text-[9px]" style={{ color: '#64748b' }}>Total Students</div>
              </div>
              <div className="p-2 rounded-lg text-center" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                <div className="text-lg font-bold" style={{ color: '#3b82f6' }}>52</div>
                <div className="text-[9px]" style={{ color: '#64748b' }}>Teachers</div>
              </div>
            </div>
            <div className="p-2 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              <div className="text-[10px] font-medium mb-2" style={{ color: '#64748b' }}>School-wide Engagement</div>
              <div className="flex items-end gap-1 h-12">
                {[65, 72, 80, 75, 88, 92, 85].map((val, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t transition-all"
                    style={{ height: `${val}%`, background: i === 6 ? '#f59e0b' : 'rgba(245, 158, 11, 0.3)' }}
                  />
                ))}
              </div>
              <div className="flex justify-between text-[8px] mt-1" style={{ color: '#64748b' }}>
                <span>Mon</span>
                <span>Today</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
              <span className="text-[10px]" style={{ color: '#10b981' }}>↑ 12% engagement this week</span>
              <span className="text-sm">📈</span>
            </div>
          </div>
        );
    }
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '320px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between transition-all duration-300"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: `${getActiveColor()}10`,
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Unified Dashboard
        </span>
        <div className="flex items-center gap-1">
          <span className="text-sm">{roles.find(r => r.role === activeRole)?.icon}</span>
          <span className="text-[10px] font-medium" style={{ color: getActiveColor() }}>
            {roles.find(r => r.role === activeRole)?.label} View
          </span>
        </div>
      </div>

      {/* Role Selector */}
      <div className="px-3 py-2 flex gap-1" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
        {roles.map(role => (
          <button
            key={role.role}
            onClick={(e) => {
              e.stopPropagation();
              setIsTransitioning(true);
              setTimeout(() => {
                setActiveRole(role.role);
                setIsTransitioning(false);
              }, 150);
            }}
            className="flex-1 py-1.5 rounded-lg text-center transition-all duration-200"
            style={{
              background: activeRole === role.role ? `${role.color}20` : 'transparent',
              border: activeRole === role.role ? `1px solid ${role.color}40` : '1px solid transparent',
            }}
          >
            <span className="text-sm">{role.icon}</span>
            <div
              className="text-[8px] mt-0.5"
              style={{ color: activeRole === role.role ? role.color : '#64748b' }}
            >
              {role.label}
            </div>
          </button>
        ))}
      </div>

      {/* Dashboard Content */}
      <div
        className="p-3 transition-all duration-300"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'translateY(10px)' : 'translateY(0)',
        }}
      >
        {renderDashboardContent()}
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <span className="text-[10px]" style={{ color: '#64748b' }}>
          One platform, 4 experiences
        </span>
        <div className="flex gap-1">
          {roles.map(role => (
            <div
              key={role.role}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{
                background: activeRole === role.role ? role.color : 'rgba(255, 255, 255, 0.2)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${getActiveColor()}E6 0%, rgba(6, 182, 212, 0.9) 100%)`,
            boxShadow: `0 8px 32px ${getActiveColor()}66`,
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default UnifiedDashboardPreview;
